import type { BotLevel } from "../constants";
import type { Rng } from "../rng";
import { GameError, type GameEvent, type TurnGameModule, type Winner } from "./types";

/**
 * Yatzy – the Scandinavian dice game (2–4 players): 5 dice, up to 3 throws per turn, 15 categories, 15 rounds.
 *
 * EduBattle rule: every turn starts with a question. A RIGHT answer gives all three throws (the first throw is rolled for you,
 * then you may hold dice and roll the others up to two more times). A WRONG answer is never a "pass" – you still have to fill in a
 * category – but you only get ONE throw, so the dice are not in your favour.
 */

export const YATZY_CATS = [
  { id: "ones", label: "Enere", hint: "Summen af alle 1'ere", upper: true, face: 1 },
  { id: "twos", label: "Toere", hint: "Summen af alle 2'ere", upper: true, face: 2 },
  { id: "threes", label: "Treere", hint: "Summen af alle 3'ere", upper: true, face: 3 },
  { id: "fours", label: "Firere", hint: "Summen af alle 4'ere", upper: true, face: 4 },
  { id: "fives", label: "Femmere", hint: "Summen af alle 5'ere", upper: true, face: 5 },
  { id: "sixes", label: "Seksere", hint: "Summen af alle 6'ere", upper: true, face: 6 },
  { id: "pair", label: "Ét par", hint: "To ens – summen af det højeste par", upper: false, face: 0 },
  { id: "twopairs", label: "To par", hint: "To forskellige par – summen af begge", upper: false, face: 0 },
  { id: "three", label: "Tre ens", hint: "Tre ens – summen af de tre", upper: false, face: 0 },
  { id: "four", label: "Fire ens", hint: "Fire ens – summen af de fire", upper: false, face: 0 },
  { id: "small", label: "Lille straight", hint: "1-2-3-4-5 giver 15", upper: false, face: 0 },
  { id: "large", label: "Stor straight", hint: "2-3-4-5-6 giver 20", upper: false, face: 0 },
  { id: "house", label: "Fuldt hus", hint: "Tre ens og to ens – summen af alle", upper: false, face: 0 },
  { id: "chance", label: "Chance", hint: "Summen af alle terninger", upper: false, face: 0 },
  { id: "yatzy", label: "Yatzy", hint: "Fem ens giver 50", upper: false, face: 0 },
] as const;
export type YatzyCat = (typeof YATZY_CATS)[number]["id"];
export const CAT_COUNT = YATZY_CATS.length;
export const UPPER_COUNT = 6;
export const BONUS_LIMIT = 63;
export const BONUS_POINTS = 50;
export const DICE = 5;

const counts = (dice: number[]): number[] => {
  const c = [0, 0, 0, 0, 0, 0, 0];
  for (const d of dice) c[d]!++;
  return c;
};

/** Points the given dice are worth in category index `cat` (0..14). */
export function scoreFor(cat: number, dice: number[]): number {
  const c = counts(dice);
  const sum = dice.reduce((a, b) => a + b, 0);
  const def = YATZY_CATS[cat]!;
  if (def.upper) return c[def.face]! * def.face;
  switch (def.id) {
    case "pair": {
      for (let f = 6; f >= 1; f--) if (c[f]! >= 2) return f * 2;
      return 0;
    }
    case "twopairs": {
      const faces: number[] = [];
      for (let f = 6; f >= 1; f--) if (c[f]! >= 2) faces.push(f);
      return faces.length >= 2 ? faces[0]! * 2 + faces[1]! * 2 : 0;
    }
    case "three": {
      for (let f = 6; f >= 1; f--) if (c[f]! >= 3) return f * 3;
      return 0;
    }
    case "four": {
      for (let f = 6; f >= 1; f--) if (c[f]! >= 4) return f * 4;
      return 0;
    }
    case "small": return [1, 2, 3, 4, 5].every((f) => c[f] === 1) ? 15 : 0;
    case "large": return [2, 3, 4, 5, 6].every((f) => c[f] === 1) ? 20 : 0;
    case "house": {
      let three = 0, two = 0;
      for (let f = 1; f <= 6; f++) { if (c[f] === 3) three = f; else if (c[f] === 2) two = f; }
      return three && two ? sum : 0;
    }
    case "chance": return sum;
    case "yatzy": return c.some((x) => x === 5) ? 50 : 0;
  }
  return 0;
}

export const upperSum = (sheet: (number | null)[]) => sheet.slice(0, UPPER_COUNT).reduce<number>((a, b) => a + (b ?? 0), 0);
export const hasBonus = (sheet: (number | null)[]) => upperSum(sheet) >= BONUS_LIMIT;
export const totalOf = (sheet: (number | null)[]) => sheet.reduce<number>((a, b) => a + (b ?? 0), 0) + (hasBonus(sheet) ? BONUS_POINTS : 0);
const filled = (sheet: (number | null)[]) => sheet.filter((x) => x !== null).length;

export interface YatzyState {
  n: number;
  sheets: (number | null)[][];
  turn: number;
  stage: "ask" | "play";
  dice: number[];
  throwsLeft: number;
  rollNo: number;
  turns: number;
  winner: Winner;
  last: { seat: number; cat: number; points: number; dice: number[] } | null;
}
export type YatzyAction = { type: "roll"; held: boolean[] } | { type: "score"; cat: number };

export interface YatzyView {
  you: number;
  turn: number;
  stage: "ask" | "play";
  dice: number[];
  throwsLeft: number;
  rollNo: number;
  round: number;
  rounds: number;
  sheets: (number | null)[][];
  totals: number[];
  bonus: boolean[];
  /** points each open category would give right now (only for the seat that is playing) */
  potential: (number | null)[];
  winner: Winner;
  last: YatzyState["last"];
}

const rollDie = (rng: Rng) => 1 + Math.floor(rng() * 6);
const nextSeat = (s: YatzyState, seat: number) => (seat + 1) % s.n;
const round = (s: YatzyState) => Math.min(CAT_COUNT, Math.floor(s.turns / s.n) + 1);

function finishIfDone(s: YatzyState): GameEvent[] {
  if (!s.sheets.every((sh) => filled(sh) === CAT_COUNT)) return [];
  const totals = s.sheets.map(totalOf);
  const best = Math.max(...totals);
  const leaders = totals.map((t, i) => (t === best ? i : -1)).filter((i) => i >= 0);
  s.winner = leaders.length === 1 ? leaders[0]! : "draw";
  return [{ kind: "over", data: { totals } }];
}

function fill(s: YatzyState, seat: number, cat: number): GameEvent[] {
  const sheet = s.sheets[seat]!;
  const points = scoreFor(cat, s.dice);
  const hadBonus = hasBonus(sheet);
  sheet[cat] = points;
  const ev: GameEvent[] = [{ kind: "score", seat, data: { cat, points } }];
  if (YATZY_CATS[cat]!.id === "yatzy" && points === 50) ev.push({ kind: "yatzy", seat });
  if (!hadBonus && hasBonus(sheet)) ev.push({ kind: "bonus", seat });
  s.last = { seat, cat, points, dice: s.dice.slice() };
  s.turns++;
  s.stage = "ask";
  s.throwsLeft = 0;
  s.turn = nextSeat(s, seat);
  ev.push(...finishIfDone(s));
  return ev;
}

/** How much a category is typically worth when it is filled – used to judge what to fill and what to chase. */
const PAR = [2.1, 5.3, 8.6, 12.2, 15.7, 19.2, 10.5, 15, 11, 6, 9, 11, 15, 23, 12];
function bestFill(sheet: (number | null)[], dice: number[]): { cat: number; gain: number } {
  let cat = -1, gain = -Infinity;
  for (let i = 0; i < CAT_COUNT; i++) {
    if (sheet[i] !== null) continue;
    let g = scoreFor(i, dice) - PAR[i]!;
    if (i < UPPER_COUNT) g += (scoreFor(i, dice) - 3 * YATZY_CATS[i]!.face) * 0.35; // keep the 63-point bonus in sight
    if (g > gain) { gain = g; cat = i; }
  }
  return { cat, gain };
}
export const autoCategory = (sheet: (number | null)[], dice: number[]) => bestFill(sheet, dice).cat;

export const yatzy: TurnGameModule<YatzyState, YatzyAction, YatzyView> = {
  id: "yatzy",
  create(rng, players) {
    const n = Math.max(2, Math.min(4, players));
    return {
      n,
      sheets: Array.from({ length: n }, () => Array.from({ length: CAT_COUNT }, () => null)),
      turn: Math.floor(rng() * n),
      stage: "ask",
      dice: [1, 2, 3, 4, 5],
      throwsLeft: 0,
      rollNo: 0,
      turns: 0,
      winner: null,
      last: null,
    };
  },
  phase: (s) => (s.winner !== null ? "over" : "play"),
  turn: (s) => s.turn,
  setupSeats: () => [],
  needsQuestion: (s) => (s.winner === null && s.stage === "ask" ? "yatzy" : null),
  continues: (s) => s.winner === null && s.stage === "play",

  resolve(s, seat, correct, rng) {
    if (s.winner !== null || seat !== s.turn || s.stage !== "ask") return [];
    s.dice = Array.from({ length: DICE }, () => rollDie(rng));
    s.rollNo++;
    s.stage = "play";
    s.throwsLeft = correct ? 2 : 0;
    return [{ kind: "roll", seat, data: { dice: s.dice.slice(), left: s.throwsLeft, first: true } }];
  },

  apply(s, seat, a, rng) {
    if (s.winner !== null) throw new GameError("Spillet er slut.");
    if (seat !== s.turn) throw new GameError("Det er ikke din tur.");
    if (s.stage !== "play") throw new GameError("Svar på spørgsmålet først, så slår du terningerne.");
    if (a?.type === "roll") {
      if (s.throwsLeft <= 0) throw new GameError("Du har ikke flere slag – vælg en kategori.");
      if (!Array.isArray(a.held) || a.held.length !== DICE) throw new GameError("Ugyldigt valg af terninger.");
      if (a.held.every(Boolean)) throw new GameError("Slip mindst én terning for at slå igen – eller vælg en kategori.");
      s.dice = s.dice.map((d, i) => (a.held[i] ? d : rollDie(rng)));
      s.throwsLeft--;
      s.rollNo++;
      return [{ kind: "roll", seat, data: { dice: s.dice.slice(), left: s.throwsLeft, held: a.held.map(Boolean) } }];
    }
    if (a?.type === "score") {
      if (!Number.isInteger(a.cat) || a.cat < 0 || a.cat >= CAT_COUNT) throw new GameError("Vælg en kategori.");
      if (s.sheets[seat]![a.cat] !== null) throw new GameError("Den kategori er allerede udfyldt.");
      return fill(s, seat, a.cat);
    }
    throw new GameError("Ukendt handling.");
  },
  penalty(s, seat, rng) {
    return yatzy.resolve!(s, seat, false, rng);
  },
  /** Action timeout: the best open category is filled in. */
  pass(s, seat) {
    if (s.stage !== "play") return [];
    return fill(s, seat, autoCategory(s.sheets[seat]!, s.dice));
  },
  winner: (s) => s.winner,
  view(s, seat): YatzyView {
    const mine = s.turn === seat && s.stage === "play" && s.winner === null;
    return {
      you: seat,
      turn: s.turn,
      stage: s.stage,
      dice: s.dice.slice(),
      throwsLeft: s.throwsLeft,
      rollNo: s.rollNo,
      round: round(s),
      rounds: CAT_COUNT,
      sheets: s.sheets.map((x) => x.slice()),
      totals: s.sheets.map(totalOf),
      bonus: s.sheets.map(hasBonus),
      potential: s.sheets[seat]!.map((v, i) => (mine && v === null ? scoreFor(i, s.dice) : null)),
      winner: s.winner,
      last: s.last,
    };
  },

  bot(view, seat, level: BotLevel, rng): YatzyAction {
    const sheet = view.sheets[seat]!;
    const dice = view.dice;
    const stop = bestFill(sheet, dice);
    if (view.throwsLeft <= 0) return { type: "score", cat: stop.cat };
    if (level === "easy") {
      // keep the most common face, stop when it already looks decent
      if (rng() < 0.25 || stop.gain > 8) return { type: "score", cat: stop.cat };
      const c = counts(dice);
      let face = 1;
      for (let f = 1; f <= 6; f++) if (c[f]! > c[face]! || (c[f] === c[face] && f > face)) face = f;
      const held = dice.map((d) => d === face);
      if (held.every(Boolean)) return { type: "score", cat: stop.cat };
      return { type: "roll", held };
    }
    // medium / hard: look at every set of held dice and estimate the value of rolling the rest once
    const samples = level === "hard" ? 36 : 12;
    let bestHeld: boolean[] | null = null;
    let bestEv = -Infinity;
    for (let mask = 0; mask < 31; mask++) { // mask 31 = hold everything (that is "stop")
      const held = dice.map((_, i) => ((mask >> i) & 1) === 1);
      let ev = 0;
      for (let k = 0; k < samples; k++) {
        const nd = dice.map((d, i) => (held[i] ? d : rollDie(rng)));
        ev += bestFill(sheet, nd).gain;
      }
      ev /= samples;
      // with two throws left, rolling is worth a bit more than a single-step estimate says
      if (view.throwsLeft >= 2) ev += 1.2;
      if (ev > bestEv) { bestEv = ev; bestHeld = held; }
    }
    if (stop.gain >= bestEv - (level === "hard" ? 0 : 1.5) || !bestHeld) return { type: "score", cat: stop.cat };
    return { type: "roll", held: bestHeld };
  },
};
